import { Cross } from '@/components/icons';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import { Button } from '@/components/ui/button';
import { useModalContext } from '@/components/ui/modal-provider';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { getTrialRemainingDays } from '@/lib/entitlements';

const DISMISSED_TRIAL_BANNER_KEY = 'dashboard_trial_banner_dismissed_period_end';

type TrialBannerProps = {
    periodEnd?: string | null;
};

const getTrialMessage = (remainingDays: number | null) => {
    if (remainingDays === null) {
        return 'Seu teste gratuito está ativo';
    }

    if (remainingDays === 0) {
        return 'Seu teste gratuito termina hoje';
    }

    if (remainingDays === 1) {
        return 'Seu teste gratuito termina em 1 dia';
    }

    return `Seu teste gratuito termina em ${remainingDays} dias`;
};

const getMobileTrialMessage = (remainingDays: number | null) => {
    if (remainingDays === null) {
        return 'Teste grátis ativo';
    }

    if (remainingDays === 0) {
        return 'Teste grátis termina hoje';
    }

    if (remainingDays === 1) {
        return 'Teste grátis: 1 dia restante';
    }

    return `Teste grátis: ${remainingDays} dias restantes`;
};

export const TrialBanner = ({ periodEnd }: TrialBannerProps) => {
    const { openModal, closeModal } = useModalContext();
    const [dismissedPeriodEnd, setDismissedPeriodEnd] = useLocalStorage<string | null>(
        DISMISSED_TRIAL_BANNER_KEY,
        null,
    );
    const periodKey = periodEnd ?? 'active-trial';

    if (dismissedPeriodEnd === periodKey) return null;

    const remainingDays = getTrialRemainingDays(periodEnd);
    const handleOpenPlansModal = () => {
        openModal({
            title: 'Conheça nossos planos',
            content: (
                <SubscriptionPlansModal
                    close={closeModal}
                    description="Escolha um plano para manter o acesso completo ao LaudoPsi sem interrupções."
                    closeLabel="Fechar"
                />
            ),
            size: 'xl',
        });
    };

    return (
        <div className="sticky top-0 z-30 flex min-h-9 w-full items-center justify-center border-b border-brand/10 bg-card py-1.5 pl-3 pr-10 text-center text-xs font-semibold text-brand shadow-elevation-1 sm:px-10 sm:text-sm">
            <p className="leading-6">
                <span className="sm:hidden">{getMobileTrialMessage(remainingDays)}</span>
                <span className="hidden sm:inline">{getTrialMessage(remainingDays)}</span>{' '}
                <span className="text-brand/40 sm:hidden">·</span>
                <span className="hidden text-brand/40 sm:inline">-</span>{' '}
                <button
                    type="button"
                    onClick={handleOpenPlansModal}
                    className="underline decoration-brand/40 underline-offset-4 transition-colors hover:text-brand/80 hover:decoration-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                >
                    <span className="sm:hidden">Assinar</span>
                    <span className="hidden sm:inline">Assinar plano</span>
                </button>
            </p>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 size-7 -translate-y-1/2 rounded-full text-brand/70 hover:bg-brand/10 hover:text-brand"
                onClick={() => setDismissedPeriodEnd(periodKey)}
                aria-label="Fechar aviso do teste gratuito"
            >
                <Cross className="size-4" />
            </Button>
        </div>
    );
};
